import type { Ref } from 'vue';
import type { Subscription } from 'rxjs';
import type {
  MeasureSelectionUpdatedPayload,
  Viewer,
} from '@puppycad/cad-core';
import type {
  FeatureTreeNodeMeta,
  FeatureTreeObjectSummary,
} from '@/hooks/feature-tree/feature-tree-types';
import {
  resolveMeasureSelectionTreeProjection,
} from '@/hooks/feature-tree/resolve-measure-selection-tree-projection';
import type {
  MeasureSelectionTreeGlbLookup,
} from '@/hooks/feature-tree/resolve-measure-selection-tree-projection';
import {
  isMeasureToTreeSyncSuppressed,
  runMeasureToTreeSelectionSync,
} from '@/hooks/feature-tree/measure-feature-tree-sync-guard';

/** 当前设计树选中是否由测量同步写入。 */
let measureOwnedTreeSelection = false;

export interface UseEditorMeasureTreeSelectionSyncOptions {
  /** 获取 Viewer 实例。 */
  getViewer: () => Viewer | null;
  /** 设计树节点元数据映射。 */
  featureTreeNodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>;
  /** GLB 设计树绑定查询器。 */
  glbFeatureTree: MeasureSelectionTreeGlbLookup;
  /** 当前选中树 key。 */
  selectedKeys: Ref<string[]>;
  /** 当前选中对象摘要。 */
  selectedObject: Ref<FeatureTreeObjectSummary | null>;
  /** 是否允许同步。 */
  canSync?: () => boolean;
}

/**
 * 测量模式设计树选中同步器。
 *
 * 设计说明：
 * 1. 仅订阅 cad-core `MeasureSelectionUpdated`，负责场景测量选中 → 设计树高亮；
 * 2. 轻量写入 selectedKeys/selectedObject，禁止回调 handleFeatureSelect 触发副作用；
 * 3. 与 imported-model 同步器互斥，由 Editor 通过 canSync 控制生效范围。
 */
export function useEditorMeasureTreeSelectionSync(
  options: UseEditorMeasureTreeSelectionSyncOptions,
) {
  const canSync = options.canSync ?? (() => true);
  let selectionUpdatedSubscription: Subscription | null = null;

  /**
   * 处理测量选中集变更。
   *
   * @param payload 测量选中集变更事件。
   */
  function handleMeasureSelectionUpdated(payload: MeasureSelectionUpdatedPayload): void {
    if (!canSync() || isMeasureToTreeSyncSuppressed()) {
      return;
    }

    if (!payload.activeAnchor) {
      clearMeasureOwnedTreeSelection();
      return;
    }

    const viewer = options.getViewer();
    if (!viewer) {
      return;
    }

    const hint = viewer.buildMeasureSelectionTreeHintFromEntityRef(payload.activeAnchor);
    const projection = resolveMeasureSelectionTreeProjection({
      viewer,
      objectId: payload.activeAnchor.objectId,
      hint,
      featureTreeNodeMap: options.featureTreeNodeMap,
      glbFeatureTree: options.glbFeatureTree,
    });
    if (!projection) {
      return;
    }

    applyTreeProjection(projection);
  }

  /**
   * 轻量写入设计树选中态。
   *
   * @param projection 设计树投影。
   */
  function applyTreeProjection(projection: {
    treeKeys: string[];
    selectedObject: FeatureTreeObjectSummary | null;
  }): void {
    const currentKey = options.selectedKeys.value[0] ?? '';
    const nextKey = projection.treeKeys[0] ?? '';
    if (
      options.selectedKeys.value.length === projection.treeKeys.length
      && currentKey === nextKey
      && options.selectedKeys.value.every((key, index) => key === projection.treeKeys[index])
    ) {
      measureOwnedTreeSelection = true;
      return;
    }

    runMeasureToTreeSelectionSync(() => {
      options.selectedKeys.value = [...projection.treeKeys];
      options.selectedObject.value = projection.selectedObject;
      measureOwnedTreeSelection = true;
    });
  }

  /**
   * 清空由测量同步持有的设计树选中态。
   */
  function clearMeasureOwnedTreeSelection(): void {
    if (!measureOwnedTreeSelection) {
      return;
    }

    runMeasureToTreeSelectionSync(() => {
      options.selectedKeys.value = [];
      options.selectedObject.value = null;
      measureOwnedTreeSelection = false;
    });
  }

  /**
   * 绑定 Viewer 测量选中事件订阅。
   *
   * @param viewer Viewer 实例。
   */
  function subscribe(viewer: Viewer): void {
    unsubscribe();
    selectionUpdatedSubscription = viewer.observeMeasureSelectionUpdated().subscribe(
      (payload) => handleMeasureSelectionUpdated(payload),
    );
  }

  /**
   * 取消测量选中事件订阅。
   */
  function unsubscribe(): void {
    selectionUpdatedSubscription?.unsubscribe();
    selectionUpdatedSubscription = null;
    measureOwnedTreeSelection = false;
  }

  return {
    handleMeasureSelectionUpdated,
    subscribe,
    unsubscribe,
    clearMeasureOwnedTreeSelection,
  };
}
